import { cn, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';
import { TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import {
  BenchmarkType,
  IndicatorName,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { ChartTooltip, ChartTooltipTextPrimary } from '@/components/Charts/components/Tooltip';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { getStyleForBenchmark } from '@/utils/getStyleForBenchmark';

export const BENCHMARK_TO_BENCHMARK_TYPE = {
  '-1': BenchmarkType.low,
  '-2': BenchmarkType.very_low,
  '0': BenchmarkType.moderate,
  '1': BenchmarkType.high,
  '2': BenchmarkType.very_high,
};

export const LATEST_FACT_TYPES = {
  [IndicatorName.forest_intelligence_sla_percentile]: IndicatorName.forest_intelligence_sla_comparison,
  [IndicatorName.forest_intelligence_lnc_percentile]: IndicatorName.forest_intelligence_lnc_comparison,
  [IndicatorName.forest_intelligence_lpc_percentile]: IndicatorName.forest_intelligence_lpc_comparison,
  [IndicatorName.forest_intelligence_functional_diversity_percentile]:
    IndicatorName.forest_intelligence_functional_diversity_comparison,
} as const;

export type FACT_TYPE = keyof typeof LATEST_FACT_TYPES;
export type BENCHMARK_FACT_TYPE = (typeof LATEST_FACT_TYPES)[FACT_TYPE];
export type BENCHMARK_FACT_VALUE_TYPE = keyof typeof BENCHMARK_TO_BENCHMARK_TYPE;

const GraphTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  const data = payload?.[0];
  const fact = data?.payload as ForestIntelligenceChartElement | undefined;

  const factBenchmark = fact?.benchmark;
  const benchmarkType = BENCHMARK_TO_BENCHMARK_TYPE[String(factBenchmark?.value) as BENCHMARK_FACT_VALUE_TYPE];

  const factValue = fact?.value as number | undefined;

  const forestIntelligenceValue = !isNullish(factValue)
    ? `${getDisplayNumber(factValue, window.navigator.language)} %`
    : t('global.analysis.noData');

  if (!active || !payload?.length) return null;

  return (
    <ChartTooltip>
      <Stack className='flex flex-col gap-1 justify-start items-start'>
        <BenchmarkPill benchmarkType={benchmarkType} />
        <Stack className='flex flex-row gap-1 justify-between w-full'>
          <ChartTooltipTextPrimary>
            {t(
              `shared.ncaDetail.details.forestIntelligence.labels.component_benchmarks.${factBenchmark?.name as BENCHMARK_FACT_TYPE}.${String(factBenchmark?.value ?? 0) as BENCHMARK_FACT_VALUE_TYPE}`,
            )}
          </ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{forestIntelligenceValue}</ChartTooltipTextPrimary>
        </Stack>
        <span className='typography-caption text-text-secondary'>
          {t(
            `shared.ncaDetail.details.forestIntelligence.explainers.component_benchmarks.${factBenchmark?.name as BENCHMARK_FACT_TYPE}.${String(factBenchmark?.value ?? 0) as BENCHMARK_FACT_VALUE_TYPE}`,
          )}
        </span>
      </Stack>
    </ChartTooltip>
  );
};

const AxisTick = (props: {
  x?: number;
  y?: number;
  cx?: number;
  cy?: number;
  payload?: { value: string };
  data?: ForestIntelligenceChartElement[];
}) => {
  const { t } = useTranslation();
  const { x = 0, y = 0, cx = 0, cy = 0, payload, data = [] } = props;

  const value = payload?.value ?? '';
  const fact = data.find((d) => d.name === value);

  if (!fact) return null;

  const dx = x - cx;
  const dy = y - cy;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const ux = dx / len;
  const labelWidth = 160;
  const anchorX = x + ux * 14;
  const labelY = y + (dy / len) * 14 - 10;

  let labelX = anchorX - labelWidth / 2;
  let justify = 'justify-center';
  if (ux < -0.3) {
    labelX = anchorX - labelWidth;
    justify = 'justify-end';
  } else if (ux > 0.3) {
    labelX = anchorX;
    justify = 'justify-start';
  }

  return (
    <foreignObject x={labelX} y={labelY} width={labelWidth} height={72} style={{ overflow: 'visible' }}>
      <div className={`flex flex-row items-start ${justify}`}>
        <div className='w-fit rounded-lg p-1 bg-neutral-black-4 typography-caption font-semibold text-text-primary whitespace-pre-wrap capitalize'>
          {t(`shared.ncaDetail.details.forestIntelligence.labels.components.${value as FACT_TYPE}`)}
        </div>
      </div>
    </foreignObject>
  );
};

type ForestIntelligenceChartElement = {
  name: string;
  value: number;
  benchmark: { name: string; value: number };
};

type ForestIntelligenceChartProps = {
  styles?: Record<string, string>;
  facts: PlotReportFactElement[];
};

export const useForestIntelligenceChart = (props: ForestIntelligenceChartProps) => {
  const { t } = useTranslation();
  const { facts } = props;

  const chartData: ForestIntelligenceChartElement[] = Object.keys(LATEST_FACT_TYPES).flatMap((factType) => {
    const factValue = getFactFromFactArray(facts, factType)?.value;
    if (isNullish(factValue) || typeof factValue !== 'number') return [];

    const benchmarkFactType = LATEST_FACT_TYPES[factType as keyof typeof LATEST_FACT_TYPES];
    const benchmarkValue = Math.round(getFactFromFactArray(facts, benchmarkFactType)?.value as number);

    return [{ name: factType, value: factValue, benchmark: { name: benchmarkFactType, value: benchmarkValue } }];
  });

  const ecoregionFact = getFactFromFactArray(facts, IndicatorName.forest_intelligence_ecoregion_id);
  const ecoregionLabel = ecoregionFact?.labels?.filter(
    (label) => label.name === PlotReportFactLabelNameEnum.class_name,
  )?.[0]?.value as string;

  const noChartData = chartData.length === 0;

  return {
    name: 'forest_intelligence_card',
    type: WidgetType.o_card,
    visible: !noChartData,
    relatedData: {
      'data-testid': 'forest-intelligence-graph-tile',
      header: t('shared.ncaDetail.details.forestIntelligence.explainers.forestIntelligence.title'),
      headerTooltip: t('shared.ncaDetail.details.forestIntelligence.explainers.forestIntelligence.body'),
      styles: {
        root: cn('h-full rounded-2xl w-full', props?.styles?.root ?? ''),
        header: cn(
          'typography-overline pt-4 pl-4 justify-center items-center text-text-secondary border-none',
          props?.styles?.header ?? '',
        ),
        body: cn('h-full py-0 flex flex-col justify-center gap-4 border-none', props?.styles?.body ?? ''),
      },
    },
    widgets: [
      {
        name: 'forest_intelligence_value',
        type: WidgetType.a_text,
        visible: true,
        relatedData: {
          text: ecoregionLabel,
          className: 'typography-body1 text-text-primary pl-0',
        },
      },
      {
        name: 'forest_intelligence_graph',
        type: WidgetType.o_composite_graph,
        visible: true,
        relatedData: {
          'data-testid': 'forest-intelligence-radar-chart',
          data: chartData,
          chartConfig: [
            {
              shape: ChartShape.radar,
              dataKey: 'value',
              fill: 'url(#forest_intelligence_score_gradient)',
              stroke: getStyleForBenchmark(BenchmarkType.low).primary,
              dot: false,
              key: 'forest_intelligence_score',
              id: 'forest_intelligence_score',
              gradient: {
                id: 'forest_intelligence_score_gradient',
                cx: '50%',
                cy: '50%',
                r: '30%',
                gradientUnits: 'userSpaceOnUse',
                stops: [
                  { offset: '0%', stopColor: getStyleForBenchmark(BenchmarkType.very_low).primary, stopOpacity: 0.8 },
                  { offset: '25%', stopColor: getStyleForBenchmark(BenchmarkType.low).primary, stopOpacity: 0.8 },
                  { offset: '50%', stopColor: getStyleForBenchmark(BenchmarkType.moderate).primary, stopOpacity: 0.8 },
                  { offset: '75%', stopColor: getStyleForBenchmark(BenchmarkType.high).primary, stopOpacity: 0.8 },
                  {
                    offset: '100%',
                    stopColor: getStyleForBenchmark(BenchmarkType.very_high).primary,
                    stopOpacity: 0.8,
                  },
                ],
              },
            },
          ],
          xAxisProps: {
            dataKey: 'name',
            tick: (tickProps: Parameters<typeof AxisTick>[0]) => <AxisTick {...tickProps} data={chartData} />,
            outerRadius: '60%',
          },
          yAxisProps: {
            domain: [0, 100],
            tickCount: 5,
            tick: false,
            axisLine: false,
          },
          tooltip: GraphTooltip,
        },
      },
    ],
  };
};
